import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import {DeliveryAddress} from 'shared/types/DeliveryAddress';
import {AddressCard} from 'components/customer/Settings/address/AddressCard';
import styled from 'styled-components';
import {Card, Body} from 'shared/components/Card';
import {CBCButton, Icon} from 'shared/helpers';
import {useGetDeliveryAddressQuery} from 'components/customer/Settings/store/settingsApi';
import {CustomAddress} from 'components/customer/Job/CustomAddress';
import CustomRadio from 'components/customer/Job/components/CustomRadio';
import {JobForm} from 'components/customer/Job/entity/JobForm';
import {useFormikContext} from 'formik';
import {useParams} from 'react-router-dom';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {DispatchMethod} from 'hooks';
import {uniqueId} from 'lodash';

interface DeliveryAddressListProps {
    hideDeliveryPreferences?: boolean;
    showName?: boolean;
    allowSave?: boolean;
    alwaysAllowNewAddress?: boolean;
}

export interface DeliveryAddressListHandle {
    reset: () => void;
}

const emptyAddress = {
    street: '',
    postcode: '',
    state: '',
    suburb: '',
    city: '',
};

export const DeliveryAddressList = forwardRef<
    DeliveryAddressListHandle,
    DeliveryAddressListProps
>(function DeliveryAddressList(
    {
        hideDeliveryPreferences = false,
        showName = false,
        allowSave = false,
        alwaysAllowNewAddress = false,
    },
    ref
) {
    const {jobId} = useParams();
    const {job} = useJobContext() as PartialJob;
    const {data: deliveryAddressList, isFetching} =
        useGetDeliveryAddressQuery();
    const {values, setFieldValue} = useFormikContext<JobForm>();

    const [selectedItem, setSelectedItem] = useState<DeliveryAddress>({});
    const [customAddress, setCustomAddress] = useState<DeliveryAddress[]>([]);
    const [show, setShow] = useState(false);
    const [editShow, setEditShow] = useState(false);

    const selectedAddressRef = useRef<DeliveryAddress>();

    const setAddress = useCallback((address?: DeliveryAddress) => {
        if (address) {
            if (Array.isArray(address?.street)) {
                void setFieldValue('street', address.street.join(' '));
            } else {
                void setFieldValue('street', address?.street);
            }
            void setFieldValue('postcode', address?.postcode);
            void setFieldValue('state', address?.state);
            void setFieldValue('suburb', address?.suburb);
            void setFieldValue('city', address?.city ?? '');
        }
    }, []);

    const handleItemClick = useCallback(
        (item: DeliveryAddress) => () => {
            setShow(false);
            setEditShow(false);
            setSelectedItem(item);
            setAddress(item);
        },
        []
    );

    const matchStreet = (
        streetA: DeliveryAddress['street'],
        streetB: DeliveryAddress['street']
    ) => {
        if (Array.isArray(streetB) && !Array.isArray(streetA)) {
            return (
                streetA == streetB.join(' ') || streetA == streetB.join(', ')
            );
        }

        if (Array.isArray(streetA) && !Array.isArray(streetB)) {
            return (
                streetB == streetA.join(' ') || streetB == streetA.join(', ')
            );
        }

        return streetA == streetB;
    };

    const handleCancel = useCallback(() => {
        if (show) {
            setShow(false);
        }

        if (editShow) {
            setEditShow(false);
        }

        setAddress(selectedAddressRef.current);
    }, [show, editShow, setAddress]);

    const handleAddAddress = useCallback(() => {
        setShow(true);
        selectedAddressRef.current = selectedItem;
        setAddress(emptyAddress);
    }, [selectedItem]);

    const handleEditAddress = useCallback(
        (item: DeliveryAddress) => (e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            selectedAddressRef.current = item;
            setAddress(item);
            setEditShow(true);
        },
        []
    );

    const checkSelectedAddress = useCallback(
        (item: DeliveryAddress) => {
            if (item && selectedItem) {
                let selectedItemCity = null;
                if (selectedItem.city) {
                    selectedItemCity = selectedItem.city;
                }
                return (
                    matchStreet(selectedItem.street, item.street) &&
                    selectedItemCity == item.city &&
                    selectedItem.suburb == item.suburb &&
                    selectedItem.postcode == item.postcode &&
                    selectedItem.state == item.state
                );
            }
        },
        [selectedItem]
    );

    const hasCustomAddress = useMemo(() => {
        const customAddressEntries = Object.entries(customAddress ?? {}).filter(
            ([key]) => key !== 'id'
        );

        return (
            customAddressEntries.length != 0 &&
            customAddressEntries.some(([, value]) => Boolean(value))
        );
    }, [customAddress]);

    const matchAddress = useCallback(
        (address: JobForm) => (listAddress: DeliveryAddress) => {
            return (
                listAddress &&
                address &&
                (listAddress.city == null ||
                    listAddress.city == address.city) &&
                matchStreet(address.street, listAddress.street) &&
                listAddress.suburb == address.suburb &&
                listAddress.postcode == address.postcode &&
                listAddress.state == address.state
            );
        },
        []
    );

    const findAddress = useCallback(
        (list: DeliveryAddress[], address: JobForm) =>
            list.find(
                (listAddress) =>
                    listAddress?.city == address.city &&
                    matchAddress(address)(listAddress)
            ) ?? list.find(matchAddress(address)),
        [matchAddress]
    );

    useEffect(() => {
        if (show || editShow) return;

        if (
            deliveryAddressList &&
            values.street &&
            values.suburb &&
            values.postcode &&
            !isNaN(Number(values.state))
        ) {
            const addressFromList = findAddress(deliveryAddressList, values);

            if (addressFromList == undefined) {
                setCustomAddress((addresses) => {
                    const inList = addresses.find(matchAddress(values));

                    if (inList === undefined) {
                        return [
                            ...addresses,
                            {
                                id: Number(uniqueId()),
                                name: '',
                                city: values.city,
                                street: values.street,
                                suburb: values.suburb,
                                postcode: values.postcode,
                                state: values.state,
                            },
                        ];
                    } else {
                        return addresses;
                    }
                });
            } else {
                setCustomAddress((addresses) => {
                    const remaining = addresses.filter(
                        (address) => !matchAddress(values)(address)
                    );

                    return remaining.length == addresses.length
                        ? addresses
                        : remaining;
                });
            }
        }
    }, [deliveryAddressList, values, show, editShow]);

    useEffect(() => {
        if (show || editShow) return;

        if (deliveryAddressList && deliveryAddressList.length > 0) {
            if (jobId) {
                if (job?.dispatchMethod === DispatchMethod.PICKUP) {
                    const defaultAddress = deliveryAddressList.find(
                        ({is_default: isDefault}) => isDefault
                    );

                    const addressFromList = findAddress(
                        [...deliveryAddressList, ...customAddress],
                        values
                    );

                    setSelectedItem((selected) => {
                        if (Object.keys(selected).length == 0) {
                            return (
                                defaultAddress ?? addressFromList ?? selected
                            );
                        }

                        return addressFromList ?? selected;
                    });
                } else if (job?.dispatchMethod !== DispatchMethod.PICKUP) {
                    const addressFromList = findAddress(
                        [...deliveryAddressList, ...customAddress],
                        values
                    );

                    if (addressFromList) {
                        setSelectedItem(addressFromList);
                    }
                }
            } else {
                if (Object.keys(selectedItem).length == 0) {
                    setSelectedItem(deliveryAddressList[0]);
                }
            }
        } else {
            const addressFromList = findAddress(customAddress, values);

            if (addressFromList) {
                setSelectedItem(addressFromList);
            }
        }
    }, [values, job, deliveryAddressList, customAddress, show, editShow]);

    useImperativeHandle(
        ref,
        () => ({
            reset: () => {
                setShow(false);
                setEditShow(false);
                setCustomAddress([]);
            },
        }),
        []
    );

    useEffect(() => {
        if (selectedItem && Object.keys(selectedItem).length > 0) {
            setAddress(selectedItem);
        }
    }, [selectedItem]);

    if (hideDeliveryPreferences) {
        return null;
    }

    return (
        <AddressContainer>
            {isFetching ? (
                <div>Loading...</div>
            ) : (
                <StyledCard>
                    <StyledTitle>
                        <Icon iconName="Delivery-Default-White.svg" />
                        <span>Select Address</span>
                    </StyledTitle>
                    <StyledBody>
                        <Styledul>
                            {editShow ? (
                                <>
                                    <SubHeadings>Update Address</SubHeadings>
                                    <LineDiv />
                                    <CustomAddress
                                        show={editShow}
                                        handleCancel={handleCancel}
                                        showName={showName}
                                        allowSave={allowSave}
                                    />
                                </>
                            ) : null}
                            {!editShow &&
                            customAddress &&
                            customAddress.length > 0 ? (
                                <>
                                    <SubHeadings>
                                        Job Delivery Address
                                    </SubHeadings>
                                    <LineDiv />
                                </>
                            ) : null}
                            {!editShow &&
                                customAddress.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <StyledList
                                            key={item.id}
                                            onClick={handleItemClick(item)}
                                            className={
                                                selectedItem.id === item.id
                                                    ? 'selected'
                                                    : ''
                                            }
                                            $selected={selectedItem === item}>
                                            <CustomRadio
                                                name="address"
                                                checked={
                                                    selectedItem.id === item.id
                                                }
                                            />
                                            <StyledAddressCard
                                                address={item}
                                                noButton={true}
                                                editButtonFunction={handleEditAddress(
                                                    item
                                                )}
                                                isEditButtonVisible={true}
                                                className="address-selection"
                                            />
                                        </StyledList>
                                    </React.Fragment>
                                ))}
                            {!isFetching &&
                            deliveryAddressList &&
                            deliveryAddressList.length > 0 ? (
                                <>
                                    <SubHeadings>Saved Addresses</SubHeadings>
                                    <LineDiv />{' '}
                                </>
                            ) : null}
                            {deliveryAddressList
                                ? deliveryAddressList.map((item) =>
                                      item.id == -1 ? null : (
                                          <StyledList
                                              key={item.id}
                                              onClick={handleItemClick(item)}
                                              className={
                                                  checkSelectedAddress(item)
                                                      ? 'selected'
                                                      : ''
                                              }
                                              $selected={
                                                  checkSelectedAddress(item) &&
                                                  !show
                                              }>
                                              <CustomRadio
                                                  name="address"
                                                  checked={
                                                      (checkSelectedAddress(
                                                          item
                                                      ) &&
                                                          !show) ??
                                                      false
                                                  }
                                              />
                                              <StyledAddressCard
                                                  address={item}
                                                  noButton={true}
                                                  className="address-selection"
                                              />
                                              {item.is_default ? (
                                                  <Default>(Default)</Default>
                                              ) : null}
                                          </StyledList>
                                      )
                                  )
                                : null}
                            {show ? (
                                <>
                                    <SubHeadings>New Address</SubHeadings>
                                    <LineDiv />
                                    <CustomAddress
                                        show={show}
                                        handleCancel={handleCancel}
                                        showName={showName}
                                        allowSave={allowSave}
                                        showAddress={false}
                                    />
                                </>
                            ) : !hasCustomAddress || alwaysAllowNewAddress ? (
                                <CBCButton
                                    style={{
                                        margin: '12px 0 12px 12px',
                                        maxWidth: '227px',
                                    }}
                                    iconName="Button-Add.svg"
                                    className="job-button button-blue"
                                    onClick={handleAddAddress}>
                                    New Address
                                </CBCButton>
                            ) : null}
                        </Styledul>
                    </StyledBody>
                </StyledCard>
            )}
        </AddressContainer>
    );
});

const StyledList = styled.li<{$selected?: boolean}>`
    list-style-type: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    border-bottom: 1px solid #e0e0e0;
    min-height: 75px;
    padding: 10px 15px;

    ${({$selected}) =>
        $selected
            ? 'background: rgb(var(--table_row_primary));'
            : ` &:hover {
            background: #f5f5f5;
        }`}
`;

const Styledul = styled.ul`
    padding-left: 0;
    margin-bottom: 0;
`;

const StyledAddressCard = styled(AddressCard)`
    border: none;
    width: 100%;
    background: none;
    margin-top: 0;
    margin-bottom: 0;
`;

const AddressContainer = styled.div`
    width: 100%;
    margin-top: 10px;
    margin-bottom: 15px;
`;

const StyledBody = styled(Body)`
    border: 1px solid #ced4da;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    padding: 0 !important;
    background: #fff;
`;
const StyledCard = styled(Card)`
    background: white;
    margin: 0;
`;
const StyledTitle = styled(Card.Title)`
    background: rgb(var(--primary_colour));
    color: white;
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    font-weight: 900;
    padding: 5px;
    font-size: 0.9em;
    margin: 0;
    display: flex;
    align-items: center;

    > img {
        width: 29px;
        border-radius: 29px;
        padding: 2px;
    }

    > span {
        flex: 1;
        text-align: center;
    }
`;

const LineDiv = styled.div`
    border: 1px solid #ced4da;
    border-bottom: 0px;
`;

const SubHeadings = styled.div`
    font-weight: 500;
    padding: 10px;
    max-width: 175px;
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    border-bottom: 0px;
    background: rgb(var(--primary_colour));
    color: white;
    margin-top: 7px;
    margin-left: 4px;
`;

const Default = styled.div`
    font-weight: 600;
    color: #bdbdbd;
`;
